import { EPagePermission } from '../_data/permission'

import { useGetPagePermissionName } from './useGetPagePermissionName'

import type { EBtnPermission } from '../_data/permission'

import { useSiteSpecificContext } from '@/contexts/SiteSpecificContext'
import { useSessionInfo } from '@/contexts/AuthContext/hooks/sessionInfo'
import { useSignOut } from '@/contexts/AuthContext/hooks/signOut'


import { useMemo, useState, useEffect } from 'react';
import { useRouter } from '@/hooks/routes';
export function usePermission() {
  const { user } = useSiteSpecificContext()

  const { data, isLoading } = user

  const {
    hasDebugPermission,
    pagePermissions,
    btnPermissions,
    hasSuperAdminPermission,
  } = useMemo(() => {
    const email = data?.debugAccount?.email
    const permissionList = (
      data?.debugAccount?.debugPermissionsToDebugAccount || []
    ).map((v) => v.debugPermissions)

    const pagePermissions = permissionList.filter(
      (v) => v.permissionType === 'page',
    )
    const btnPermissions = permissionList.filter(
      (v) => v.permissionType === 'btn',
    )

    const hasDebugPermission = Boolean(email && pagePermissions.length)

    const hasSuperAdminPermission = !!pagePermissions.find(
      (v) => v.permissionName === EPagePermission.SuperAdmin,
    )

    return {
      hasDebugPermission,
      pagePermissions,
      btnPermissions,
      hasSuperAdminPermission,
    }
  }, [
    data?.debugAccount?.debugPermissionsToDebugAccount,
    data?.debugAccount?.email,
  ])

  return {
    hasDebugPermission,
    pagePermissions,
    btnPermissions,
    userData: data,
    hasSuperAdminPermission,
  }
}

interface IUseValidPermissionProps {
  permissionType: 'page' | 'btn'
  permissionName?: EPagePermission | EBtnPermission
}
export function useValidPermission() {
  const {
    hasDebugPermission,
    pagePermissions,
    btnPermissions,
    hasSuperAdminPermission,
  } = usePermission()

  const validPermission = (props: IUseValidPermissionProps) => {
    const { permissionType = 'page', permissionName } = props

    if (!hasDebugPermission) {
      return false
    }

    if (permissionType === 'page' && permissionName) {
      return !!(
        pagePermissions.length &&
        pagePermissions.find((v) => v.permissionName === permissionName)
      )
    }

    if (permissionType === 'btn' && permissionName) {
      return !!(
        btnPermissions.length &&
        btnPermissions.find((v) => v.permissionName === permissionName)
      )
    }

    return false
  }

  const validSuperPermission = (props: IUseValidPermissionProps) => {
    if (hasSuperAdminPermission) {
      return true
    }
    return validPermission(props)
  }

  return {
    validPermission,
    validSuperPermission,
  }
}

export function useValidPagePermission() {
  const { hasDebugPermission, userData } = usePermission()
  const { validSuperPermission } = useValidPermission()
  const { getPagePermissionName } = useGetPagePermissionName()
  const [isValidPass, setIsValidPass] = useState(false)

  const router = useRouter()

  const { sessionLoading, isSignIn } = useSessionInfo()
  const signOut = useSignOut()

  useEffect(() => {
    if (sessionLoading) {
      return
    }
    if (!isSignIn) {
      signOut({ redirect: '/' })
      return
    }
    if (userData) {
      const permissionName = getPagePermissionName()
      const hasPagePermission = validSuperPermission({
        permissionName,
        permissionType: 'page',
      })
      if (!hasDebugPermission || !hasPagePermission) {
        router.replace('/404')
      } else {
        setIsValidPass(true)
      }
    }
  }, [
    getPagePermissionName,
    hasDebugPermission,
    isSignIn,
    router,
    sessionLoading,
    signOut,
    userData,
    validSuperPermission,
  ])

  return { isValidPass }
}
